import React, { useState, cloneElement, Component } from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { CalendarIcon, MessageCircleIcon, SparklesIcon, FlameIcon } from 'lucide-react';
export function CoachingPage() {
  const handleRequestSession = (tier: string) => {
    const subject = encodeURIComponent(`Coaching Request - ${tier}`);
    const body = encodeURIComponent(`Hi Mandy!\n\nI'm interested in the ${tier} coaching option.\n\nPlease reach out so we can discuss details and get started!\n\nThank you!`);
    window.location.href = `mailto:trippinwithmandy@gmail.com?subject=${subject}&body=${body}`;
  };
  return <div className="min-h-screen pt-24 pb-24 relative" data-id="element-1062">
      {/* Full background image */}
      <img src="https://cdn.magicpatterns.com/uploads/tYcgpo9gfyKttKrKFsXuLo/ChatGPT_Image_Mar_21,_2026,_09_50_42_PM.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain object-top pointer-events-none z-0" data-id="element-1063" />
      {/* Soft overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/60 to-white/80 z-0" data-id="element-1064" />

      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden bg-gradient-to-b from-blush-50/70 to-white/60 backdrop-blur-[2px]" data-id="element-1065">
        <SparkleEffect count={80} data-id="element-1066" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-1067">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-800 mb-6 drop-shadow-sm" data-id="element-1068">
            Trippin' Through <span className="text-cta-600" data-id="element-1069">Healing</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-700 font-semibold" data-id="element-1070">
            Real-life guidance, mindset support, and confidence rebuilding—not
            therapy, but practical help navigating real-life challenges.
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-lg text-cta-600 font-bold mt-4" data-id="element-1071">
            Book your free call today
          </motion.p>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1072">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-1073">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-1074">
            <div className="aspect-square rounded-[3rem] bg-lavender-100/80 backdrop-blur-sm p-8 flex items-center justify-center relative overflow-hidden border border-lavender-200/60" data-id="element-1075">
              <SparkleEffect count={40} data-id="element-1076" />
              <div className="text-center z-10" data-id="element-1077">
                <FlameIcon className="w-16 h-16 text-cta-500 mx-auto mb-4" data-id="element-1078" />
                <h3 className="font-heading text-3xl text-plum-800 font-bold" data-id="element-1079">
                  Who It's For
                </h3>
                <ul className="mt-6 space-y-3 text-left" data-id="element-1080">
                  <li className="flex items-center gap-2 text-plum-800 font-semibold" data-id="element-1081">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-1082" />{' '}
                    You're stuck and you know it.
                  </li>
                  <li className="flex items-center gap-2 text-plum-800 font-semibold" data-id="element-1083">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-1084" />{' '}
                    You're tired of just surviving.
                  </li>
                  <li className="flex items-center gap-2 text-plum-800 font-semibold" data-id="element-1085">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-1086" />{' '}
                    You need someone who won't judge your mess.
                  </li>
                  <li className="flex items-center gap-2 text-plum-800 font-semibold" data-id="element-1087">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-1088" />{' '}
                    You're ready to do the damn work.
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white/85 backdrop-blur-sm rounded-3xl p-8 border border-blush-100 shadow-lg" data-id="element-1089">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-plum-800 mb-6" data-id="element-1090">
              What to Expect
            </h2>
            <ul className="space-y-6" data-id="element-1091">
              {[{
              title: 'Real talk, not textbook advice',
              desc: "I don't do fluff. We're going to look at your life honestly and figure out exactly what needs to change."
            }, {
              title: 'Someone who actually gives a damn',
              desc: 'When you work with me, I am fully invested in your healing and your success.'
            }, {
              title: 'Accountability with love (and tough love)',
              desc: 'I will hold your hand, but I will also call you out on your excuses. Because you deserve better.'
            }, {
              title: 'Mindset shifts that stick',
              desc: "We aren't just putting a band-aid on it. We are rewiring how you think and operate."
            }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-1092">
                  <div className="mt-1 w-2 h-2 rounded-full bg-cta-500 shrink-0" data-id="element-1093" />
                  <div data-id="element-1094">
                    <h4 className="font-bold text-plum-800 text-lg" data-id="element-1095">
                      {item.title}
                    </h4>
                    <p className="text-plum-600 font-semibold" data-id="element-1096">{item.desc}</p>
                  </div>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-blush-50/80 mt-12 relative z-10" data-id="element-1097">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1098">
          <h2 className="font-heading text-4xl font-bold text-plum-800 mb-16" data-id="element-1099">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-1100">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-blush-200" data-id="element-1101" />

            {[{
            icon: <CalendarIcon data-id="element-1102" />,
            title: '1. Book a Free Consultation Call',
            desc: "Your first consultation call is completely free. This is where we talk about what you're going through, what kind of support you need, and whether we're a good fit to work together. If you decide to continue after that, paid coaching options are available at the rates listed above."
          }, {
            icon: <MessageCircleIcon data-id="element-1103" />,
            title: '2. Get Real',
            desc: 'We map out exactly where you are and where you want to be.'
          }, {
            icon: <SparklesIcon data-id="element-1104" />,
            title: '3. Transform',
            desc: 'We do the work. You stop surviving and start living like a badass.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10" data-id="element-1105">
                <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-sm border border-blush-100 flex items-center justify-center text-cta-500 mb-6" data-id="element-1106">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'w-10 h-10'
              })}
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-800 mb-3" data-id="element-1107">
                  {step.title}
                </h3>
                <p className="text-plum-700 font-semibold" data-id="element-1108">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-4 bg-white/80 relative overflow-hidden z-10" data-id="element-1109">
        <SparkleEffect count={50} data-id="element-1110" />
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-1111">
          <div className="text-center mb-16" data-id="element-1112">
            <h2 className="font-heading text-4xl font-bold text-plum-800 mb-4" data-id="element-1113">
              Accessible pricing because everyone deserves support.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" data-id="element-1114">
            {/* Card 1 */}
            <div className="bg-white/90 backdrop-blur-sm border border-blush-100 rounded-[3rem] p-10 shadow-xl flex flex-col relative overflow-hidden" data-id="element-1115">
              <h3 className="font-heading text-2xl font-bold text-plum-800 mb-2" data-id="element-1116">
                1:1 Session
              </h3>
              <p className="text-plum-600 font-semibold mb-6" data-id="element-1117">45–60 minutes</p>
              <div className="mb-8" data-id="element-1118">
                <span className="font-heading text-4xl font-bold text-plum-800" data-id="element-1119">
                  $25–$45
                </span>
              </div>
              <p className="text-plum-700 font-semibold mb-10 flex-grow" data-id="element-1120">
                A single deep-dive session to tackle what's on your mind right
                now.
              </p>
              <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="w-full py-4 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-800 transition-colors text-center block" data-id="element-1121">
                Book Free Consultation
              </a>
            </div>

            {/* Card 2 (Most Popular) */}
            <div className="bg-gradient-to-br from-plum-900 to-plum-800 rounded-[3rem] p-10 shadow-2xl flex flex-col relative overflow-hidden transform md:-translate-y-4" data-id="element-1122">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cta-500/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-1123" />
              <div className="inline-flex self-start px-4 py-1.5 rounded-full bg-cta-500 text-white text-xs font-bold uppercase tracking-wider mb-6" data-id="element-1124">
                Most Popular
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-2" data-id="element-1125">
                3 Session Package
              </h3>
              <div className="mb-8 mt-6" data-id="element-1126">
                <span className="font-heading text-4xl font-bold text-white" data-id="element-1127">
                  $60–$100
                </span>
              </div>
              <p className="text-white/90 font-semibold mb-10 flex-grow" data-id="element-1128">
                Three sessions to build momentum and create real, lasting
                change.
              </p>
              <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="w-full py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-colors shadow-lg shadow-cta-500/30 text-center block" data-id="element-1129">
                Book Free Consultation
              </a>
            </div>

            {/* Card 3 */}
            <div className="bg-white/90 backdrop-blur-sm border border-blush-100 rounded-[3rem] p-10 shadow-xl flex flex-col relative overflow-hidden" data-id="element-1130">
              <h3 className="font-heading text-2xl font-bold text-plum-800 mb-2" data-id="element-1131">
                Monthly Support
              </h3>
              <div className="mb-8 mt-12" data-id="element-1132">
                <span className="font-heading text-4xl font-bold text-plum-800" data-id="element-1133">
                  $120–$180
                </span>
                <span className="text-plum-600 font-semibold" data-id="element-1134">/month</span>
              </div>
              <p className="text-plum-700 font-semibold mb-10 flex-grow" data-id="element-1135">
                Ongoing support with weekly check-ins to keep you on track and
                thriving.
              </p>
              <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="w-full py-4 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-800 transition-colors text-center block" data-id="element-1136">
                Book Free Consultation
              </a>
            </div>
          </div>

          <p className="text-center text-plum-700 font-semibold mt-10 max-w-2xl mx-auto" data-id="element-1137">
            Your first consultation call is free. You'll receive a Google Meet
            link after booking.
            <br data-id="element-1138" />
            <br data-id="element-1139" />
            All sessions are personalized to your needs. Once you submit your
            request, I'll reach out to schedule and confirm details.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center px-4 relative z-10" data-id="element-1140">
        <div className="max-w-2xl mx-auto bg-white/90 backdrop-blur-sm p-12 rounded-[3rem] shadow-xl border border-blush-100 relative overflow-hidden" data-id="element-1141">
          <SparkleEffect count={50} data-id="element-1142" />
          <div className="relative z-10" data-id="element-1143">
            <h2 className="font-heading text-3xl font-bold text-plum-800 mb-4" data-id="element-1144">
              Ready to invest in yourself?
            </h2>
            <p className="text-plum-700 mb-8 font-semibold" data-id="element-1145">
              Real-life guidance, mindset support, and confidence rebuilding.
              Let's figure out the right fit for you.
            </p>
            <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-lg hover:-translate-y-1" data-id="element-1146">
              <GlitterHeart size={20} variant="light" data-id="element-1147" />
              Book Free Consultation
            </a>
          </div>
        </div>
      </section>
    </div>;
}